import { useEffect, useRef, useState } from 'react'
import { client, trpc, type RouterOutput } from '@/config/trpc'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/shadcn/tooltip'
import { RefreshCw, TrendingUp } from 'lucide-react'
import { toast } from 'sonner'

type MoodboardRow = RouterOutput['moodboard']['getAll'][number]
type Suggestion = RouterOutput['topicSuggestion']['get'][number]

interface Props {
  onTopicSelect: (topic: string) => void
  // Moodboard grounding the topic suggestions (brand context assembled server-side).
  selectedMoodboard?: MoodboardRow | null
  isSelectionLoading?: boolean
}

// Stable key for the current selection — drives the query cache and guards
// against stale responses landing after the user switches moodboard.
function selectionKey(ids: { moodboardId?: number }): string {
  if (ids.moodboardId) return `m:${ids.moodboardId}`
  return 'none'
}

function SuggestionsSkeleton() {
  return (
    <div className="mx-auto grid grid-cols-1 gap-2.5 px-2 sm:grid-cols-2 sm:px-0">
      {Array.from({ length: 4 }).map((_, index) => (
        <div
          key={index}
          className="animate-once animate-fade-left"
          style={{ animationDelay: `${index * 120}ms` }}>
          <div className="bg-muted h-12 w-full animate-pulse rounded-xl" />
        </div>
      ))}
    </div>
  )
}

function mergeUniqueTopics(current: string[], next: string[]) {
  return Array.from(new Set([...current, ...next])).slice(-50)
}

export function HomeTopicSuggestions({
  onTopicSelect,
  selectedMoodboard,
  isSelectionLoading,
}: Props) {
  const [displayedSuggestions, setDisplayedSuggestions] = useState<Suggestion[]>([])
  const [seenTopics, setSeenTopics] = useState<string[]>([])
  const [isRefreshing, setIsRefreshing] = useState(false)

  const contextIds = selectedMoodboard ? { moodboardId: selectedMoodboard.id } : {}
  const key = selectionKey(contextIds)
  const latestKeyRef = useRef(key)

  useEffect(() => {
    latestKeyRef.current = key
    setIsRefreshing(false)
  }, [key])

  const {
    data: suggestions,
    isLoading,
    isFetching,
  } = trpc.topicSuggestion.get.useQuery(contextIds, {
    enabled: !isSelectionLoading,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (isSelectionLoading) return

    if (!suggestions?.length) {
      setDisplayedSuggestions([])
      setSeenTopics([])
      return
    }

    setDisplayedSuggestions(suggestions)
    setSeenTopics(suggestions.map((s) => s.topic))
    setIsRefreshing(false)
  }, [key, isSelectionLoading, suggestions])

  const handleTopicClick = (suggestion: Suggestion) => {
    // Insert the elaborate, best-practice prompt — not just the headline — so the
    // topic box gets a model of how to prompt CASSI well (angle + visual + platform).
    onTopicSelect(suggestion.prompt)
  }

  const handleRetry = async () => {
    if (isFetching || isRefreshing || displayedSuggestions.length === 0) return

    const requestKey = key
    const excludedTopics = seenTopics.slice(-50)

    setIsRefreshing(true)

    try {
      const nextSuggestions = await client.topicSuggestion.get.query({
        ...contextIds,
        existingTopics: excludedTopics,
      })

      if (latestKeyRef.current !== requestKey) {
        return
      }

      setDisplayedSuggestions(nextSuggestions)
      setSeenTopics((current) =>
        mergeUniqueTopics(
          current,
          nextSuggestions.map((s) => s.topic),
        ),
      )
    } catch {
      if (latestKeyRef.current === requestKey) {
        toast.error('Failed to refresh topic suggestions')
      }
    } finally {
      setIsRefreshing(false)
    }
  }

  const hasSuggestions = displayedSuggestions.length > 0
  const showSkeleton =
    isSelectionLoading ||
    isRefreshing ||
    ((isLoading || isFetching) && displayedSuggestions.length === 0)

  if (!showSkeleton && displayedSuggestions.length === 0 && !isLoading) {
    return null
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-center gap-1.5">
        <p className="text-muted-foreground text-center text-sm">Not sure where to start?</p>
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground h-5.5 w-5.5 inline-flex items-center justify-center rounded-full transition-colors disabled:pointer-events-none disabled:opacity-50"
          onClick={handleRetry}
          disabled={isSelectionLoading || isFetching || isRefreshing || !hasSuggestions}
          title="Refresh topic suggestions">
          <RefreshCw className={isFetching || isRefreshing ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
        </button>
      </div>

      {showSkeleton ? (
        <SuggestionsSkeleton />
      ) : (
        <TooltipProvider delayDuration={150}>
          <div className="mx-auto grid grid-cols-1 gap-2.5 px-2 sm:grid-cols-2 sm:px-0">
            {displayedSuggestions.map((suggestion, index) => {
              const colors = [
                'bg-blue-200 text-blue-900 hover:bg-blue-300 dark:bg-blue-800 dark:text-blue-100 dark:hover:bg-blue-700',
                'bg-green-200 text-green-900 hover:bg-green-300 dark:bg-green-800 dark:text-green-100 dark:hover:bg-green-700',
                'bg-purple-200 text-purple-900 hover:bg-purple-300 dark:bg-purple-800 dark:text-purple-100 dark:hover:bg-purple-700',
                'bg-orange-200 text-orange-900 hover:bg-orange-300 dark:bg-orange-800 dark:text-orange-100 dark:hover:bg-orange-700',
                'bg-pink-200 text-pink-900 hover:bg-pink-300 dark:bg-pink-800 dark:text-pink-100 dark:hover:bg-pink-700',
                'bg-indigo-200 text-indigo-900 hover:bg-indigo-300 dark:bg-indigo-800 dark:text-indigo-100 dark:hover:bg-indigo-700',
              ]

              const colorClass = colors[index % colors.length]

              return (
                <div
                  key={suggestion.topic}
                  className="animate-once animate-fade-left h-full"
                  style={{ animationDelay: `${index * 150}ms` }}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => handleTopicClick(suggestion)}
                        className={`${colorClass} flex h-full w-full cursor-pointer flex-col items-start gap-0.5 rounded-xl px-3.5 py-1.5 text-left transition-all duration-100`}>
                        {suggestion.source === 'analytics' && (
                          <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide opacity-80">
                            <TrendingUp className="h-3 w-3" />
                            Top performer
                          </span>
                        )}
                        <span className="text-xs font-medium sm:text-sm">{suggestion.topic}</span>
                        {suggestion.rationale && (
                          <span className="text-[11px] font-normal opacity-70">
                            {suggestion.rationale}
                          </span>
                        )}
                      </button>
                    </TooltipTrigger>
                    {suggestion.prompt && (
                      <TooltipContent className="max-w-xs whitespace-normal leading-relaxed">
                        <span className="text-tooltip-foreground/60 mb-1 block text-[10px] font-medium uppercase tracking-wide">
                          Inserts this prompt
                        </span>
                        {suggestion.prompt}
                      </TooltipContent>
                    )}
                  </Tooltip>
                </div>
              )
            })}
          </div>
        </TooltipProvider>
      )}
    </div>
  )
}
